import { useState, useEffect, useRef } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { NavLink, Outlet } from "react-router-dom";
import SEO from "../SEO";
import { db } from "../../firebase";
import "../../styles/SecondMarriage.css";

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("sm-revealed"); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function AnimCard({ children, delay = 0, className = "" }) {
  const ref = useReveal();
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const { left, top, width, height } = card.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;
    card.style.transform = `translateY(-6px) rotateX(${-y * 8}deg) rotateY(${x * 8}deg) scale(1.02)`;
  };
  const handleMouseLeave = (e) => {
    const card = cardRef.current;
    if (card) card.style.transform = "";
  };

  return (
    <div ref={ref} className={`sm-anim-card ${className}`} style={{ "--delay": `${delay}ms` }}>
      <div
        ref={cardRef}
        className="sm-tilt-inner"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    </div>
  );
}

function ScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      setPct((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="sm-scroll-bar" style={{ width: `${pct}%` }} />;
}

const PARTICLES = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  size: 6 + Math.random() * 10,
  x: Math.random() * 100,
  delay: Math.random() * 5,
  dur: 6 + Math.random() * 6,
}));

const EFFECTIVE_DATE = "3 September 2026";

function Nav() {
  const cls = ({ isActive }) => (isActive ? "active" : "");
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <nav className={`sm-nav${scrolled ? " sm-nav-scrolled" : ""}`}>
      <NavLink to="/secondmarriage" className="sm-brand">
        <img src="/assets/second_marriage_logo.png" alt="Second Marriage" className="sm-brand-logo" />
        Second Marriage
      </NavLink>
      <div className="sm-nav-links">
        <NavLink to="/secondmarriage" end className={cls}>
          Home
        </NavLink>
        <NavLink to="/secondmarriage/privacypolicy" className={cls}>
          Privacy
        </NavLink>
        <NavLink to="/secondmarriage/termsofservices" className={cls}>
          Terms
        </NavLink>
        <NavLink to="/secondmarriage/communityguidelines" className={cls}>
          Guidelines
        </NavLink>
        <NavLink to="/secondmarriage/faq" className={cls}>
          FAQ
        </NavLink>
        <NavLink to="/secondmarriage/contact" className={cls}>
          Support
        </NavLink>
      </div>
    </nav>
  );
}

function SmFooter() {
  return (
    <footer className="sm-footer">
      <div className="sm-footer-links">
        <NavLink to="/secondmarriage">Home</NavLink>
        <NavLink to="/secondmarriage/privacypolicy">Privacy Policy</NavLink>
        <NavLink to="/secondmarriage/termsofservices">Terms of Service</NavLink>
        <NavLink to="/secondmarriage/eula">EULA</NavLink>
        <NavLink to="/secondmarriage/communityguidelines">Community Guidelines</NavLink>
        <NavLink to="/secondmarriage/child-safety">Child Safety</NavLink>
        <NavLink to="/secondmarriage/faq">FAQ</NavLink>
        <NavLink to="/secondmarriage/contact">Support</NavLink>
      </div>
      <p>© {new Date().getFullYear()} Second Marriage · MacroMatrix (Pvt) Limited</p>
      <p style={{fontSize: "12px", marginTop: "4px", color: "var(--sm-text-2)"}}>
        Office No 4-A-I Commercial Area, PGEHS, Lahore, Pakistan &nbsp;·&nbsp; 7th Floor, Opal Tower, 701–705, BJK Blvd, Business Bay, Dubai – UAE
      </p>
    </footer>
  );
}

export function SecondMarriageLayout() {
  useEffect(() => {
    const link = document.querySelector("link[rel='icon']");
    const prev = link.href;
    link.href = "/assets/second_marriage_icon.png";
    return () => { link.href = prev; };
  }, []);
  return (
    <div className="sm-root">
      <Nav />
      <Outlet />
      <SmFooter />
    </div>
  );
}

const svgIcon = (d) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>;

const FEATURES = [
  {
    icon: svgIcon(<><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></>),
    title: "Built for second chances",
    text: "A respectful matrimonial space for divorced, widowed and never-married adults looking for a serious, marriage-minded partner.",
  },
  {
    icon: svgIcon(<><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>),
    title: "Meaningful discovery",
    text: "Browse profiles filtered by age, city, marital status, education, profession and your own partner preferences — not endless swiping.",
  },
  {
    icon: svgIcon(<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>),
    title: "Express interest",
    text: "Send a considered interest instead of a cold message. Conversations open only when both sides agree.",
  },
  {
    icon: svgIcon(<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>),
    title: "Private chat",
    text: "Text messaging unlocks after a mutual match, so your inbox stays free of unsolicited approaches.",
  },
  {
    icon: svgIcon(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>),
    title: "Photo & face verification",
    text: "Optional selfie verification helps confirm real people and reduce fake profiles — no government ID uploads required.",
  },
  {
    icon: svgIcon(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>),
    title: "Guardian (Wali) support",
    text: "Invite a trusted family member or guardian to oversee your search, in line with family and cultural expectations.",
  },
  {
    icon: svgIcon(<><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>),
    title: "Privacy you control",
    text: "Manage profile and photo visibility, phone-number search, activity status and anonymous browsing at any time.",
  },
  {
    icon: svgIcon(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>),
    title: "Safety Center",
    text: "Block, unmatch and report users or messages in a tap, with community rules and a moderation team behind every report.",
  },
  {
    icon: svgIcon(<><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/><line x1="17" y1="17" x2="22" y2="17"/></>),
    title: "Clearly labelled AI profiles",
    text: "Any AI-powered profile carries a visible \"AI Profile\" badge. Real people are never presented as AI, and AI is never presented as real.",
  },
  {
    icon: svgIcon(<><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>),
    title: "Premium membership",
    text: "Optional subscription for an ad-free experience and expanded discovery, billed through the App Store or Google Play.",
  },
  {
    icon: svgIcon(<><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>),
    title: "Stay in the loop",
    text: "Notifications for new interests, matches and messages — so you never miss a genuine connection.",
  },
  {
    icon: svgIcon(<><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></>),
    title: "Light & dark themes",
    text: "A calm, uncluttered interface designed for comfortable browsing at any hour, on iPhone and Android.",
  },
];

const STEPS = [
  {
    title: "Create your profile",
    text: "Sign up with email, Google or Apple, then share your age, city, marital status and what you are looking for.",
  },
  {
    title: "Verify & set preferences",
    text: "Add photos, verify with a selfie and tell us your partner preferences so we surface the right people.",
  },
  {
    title: "Discover & express interest",
    text: "Browse matching profiles and send an interest to anyone you would like to know better.",
  },
  {
    title: "Match, chat & take it forward",
    text: "When interest is mutual, private chat opens — with your guardian involved if you choose.",
  },
];

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await addDoc(collection(db, "sm_waitlist"), {
        email,
        createdAt: serverTimestamp(),
      });
      setDone(true);
      setEmail("");
    } catch (err) {
      console.error(err);
    }
    setSending(false);
  };

  if (done) {
    return <p className="sm-waitlist-thanks">✅ You're on the list! We'll notify you at launch.</p>;
  }

  return (
    <form onSubmit={submit} className="sm-waitlist-form">
      <input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <button type="submit" className="sm-btn sm-btn-primary" disabled={sending}>
        {sending ? "Joining…" : "Notify Me"}
      </button>
    </form>
  );
}

export function SecondMarriageHome() {
  return (
    <>
      <SEO title="Second Marriage" description="A matrimonial app for adults serious about marriage — divorced, widowed, or never married. Real profiles, guardian support, and privacy-first design." path="/secondmarriage" />
      <ScrollProgress />
      <section className="sm-hero">
        <div className="sm-particles" aria-hidden="true">
          {PARTICLES.map((p) => (
            <span
              key={p.id}
              className="sm-particle"
              style={{
                width: p.size, height: p.size,
                left: `${p.x}%`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.dur}s`,
              }}
            />
          ))}
        </div>
        <img src="/assets/second_marriage_icon.png" alt="Second Marriage" className="sm-hero-logo" />
        <span className="sm-pill">Marriage-minded. Verified. Respectful.</span>
        <h1>
          A second chance at a <span className="sm-gradient-text">life partner</span>
        </h1>
        <p>
          Second Marriage is a matrimonial app for adults who are serious about
          marriage — divorced, widowed or never married. Real profiles, private
          conversations, and safety features built in from day one.
        </p>
        <div className="sm-cta">
          <a className="sm-btn sm-btn-primary" href="#features">
            Explore features
          </a>
          <NavLink className="sm-btn sm-btn-ghost" to="/secondmarriage/contact">
            Contact us
          </NavLink>
        </div>
        <div className="sm-store-badges">
          <div className="sm-coming-badge">
            <svg className="sm-badge-icon" viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            <div>
              <small>Coming soon on</small>
              <strong>App Store</strong>
            </div>
          </div>
          <a className="sm-coming-badge" href="https://play.google.com/store/apps/details?id=second.marriage.macromatrix" target="_blank" rel="noopener noreferrer">
            <img className="sm-badge-icon" src="/assets/playstore.png" alt="Google Play" width="28" height="28" loading="lazy" />
            <div>
              <small>Get it on</small>
              <strong>Google Play</strong>
            </div>
          </a>
        </div>
      </section>

      <section className="sm-section" id="features">
        <h2>Everything the app does</h2>
        <p className="sm-sub">
          Designed around one goal: helping eligible adults find a compatible
          partner for marriage, safely.
        </p>
        <div className="sm-grid">
          {FEATURES.map((f, i) => (
            <AnimCard key={f.title} delay={i * 60} className="sm-card">
              <div className="sm-card-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </AnimCard>
          ))}
        </div>
      </section>

      <section className="sm-section alt">
        <div className="sm-section-inner">
          <h2>How it works</h2>
          <p className="sm-sub">Four steps from sign-up to a real conversation.</p>
          <div className="sm-grid sm-steps">
            {STEPS.map((s, i) => (
              <AnimCard key={s.title} delay={i * 100} className="sm-card">
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </AnimCard>
            ))}
          </div>
        </div>
      </section>

      <section className="sm-section" id="safety">
        <h2>Safety & Trust</h2>
        <p className="sm-sub">Your safety is built into every layer of the app.</p>
        <div className="sm-grid sm-safety-grid">
          {[
            { icon: svgIcon(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>), title: "Moderated Community", text: "Every reported profile and message is reviewed by our moderation team within 24 hours." },
            { icon: svgIcon(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>), title: "Selfie Verification", text: "Optional face verification confirms real people and marks verified profiles with a badge." },
            { icon: svgIcon(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>), title: "Guardian (Wali) System", text: "Invite a trusted family member to oversee your profile and conversations for added accountability." },
            { icon: svgIcon(<><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>), title: "Block & Report", text: "Instantly block or report any user or message. Blocked users can never contact you again." },
            { icon: svgIcon(<><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>), title: "Private by Default", text: "Photos, phone numbers, and activity status are hidden until you choose to share them." },
            { icon: svgIcon(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>), title: "Community Guidelines", text: "Clear rules against harassment, hate speech, scams and fake profiles — enforced consistently." },
          ].map((s, i) => (
            <AnimCard key={s.title} delay={i * 80} className="sm-card">
              <div className="sm-card-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </AnimCard>
          ))}
        </div>
      </section>

      <section className="sm-section">
        <div className="sm-banner">
          <h2>18+ only, and always your choice</h2>
          <p>
            Second Marriage is for adults aged 18 and above. Religious and
            cultural details are optional, government ID uploads are never
            required, and you can delete your account from inside the app at any
            time.
          </p>
          <div className="sm-cta" style={{ marginTop: 24 }}>
            <NavLink
              className="sm-btn sm-btn-ghost"
              to="/secondmarriage/privacypolicy"
            >
              Read the Privacy Policy
            </NavLink>
          </div>
        </div>
      </section>

      <section className="sm-section sm-waitlist-section">
        <div className="sm-waitlist">
          <h2>🔔 Get Notified When We Launch</h2>
          <p>Be the first to know when Second Marriage is available on App Store and Google Play.</p>
          <WaitlistForm />
        </div>
      </section>
    </>
  );
}

const Ul = ({ items }) => (
  <ul>
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);

export function SecondMarriagePrivacyPolicy() {
  return (
    <div className="sm-legal">
      <SEO title="Privacy Policy – Second Marriage" description="How Second Marriage collects, uses, and protects your personal data. Your privacy is built into every layer of the app." path="/secondmarriage/privacypolicy" />
      <h1>Privacy Policy for Second Marriage</h1>
      <p className="sm-meta">
        Effective Date: {EFFECTIVE_DATE} · Last Updated: {EFFECTIVE_DATE}
      </p>

      <h2>1. Introduction</h2>
      <p>
        Second Marriage (“Second Marriage,” “we,” “us,” or “our”) provides a
        matrimonial and relationship platform designed to help eligible adults
        connect with potential partners for meaningful relationships and
        marriage.
      </p>
      <p>
        This Privacy Policy explains how we collect, use, store, share, and
        protect information when you use the Second Marriage mobile application
        and related services (collectively, the “Service”).
      </p>
      <p>
        By using Second Marriage, creating an account, or accessing our services,
        you acknowledge that you have read and understood this Privacy Policy.
      </p>

      <h2>2. Who Can Use Second Marriage</h2>
      <p>Second Marriage is intended only for adults.</p>
      <p>
        You must be at least 18 years old to create an account or use the
        Service. We may collect your date of birth to help verify that you meet
        our minimum age requirement.
      </p>
      <p>
        We do not knowingly permit individuals under the age of 18 to create
        accounts or use the Service. If we become aware that an account belongs
        to an underage individual, we may suspend or delete the account and
        associated information.
      </p>

      <h2>3. Information We Collect</h2>
      <p>
        We collect information that is necessary to operate, maintain, secure,
        and improve the Second Marriage Service.
      </p>
      <h3>Account Information</h3>
      <p>When you create an account, we may collect:</p>
      <Ul
        items={[
          "Name or display name",
          "Email address",
          "Authentication information",
          "Date of birth",
          "Gender",
          "Account credentials or authentication information",
        ]}
      />
      <p>
        You may also choose to sign in using supported third-party
        authentication services, such as Google or Apple.
      </p>

      <h2>4. Profile Information</h2>
      <p>
        To provide matrimonial matching and profile discovery features, we may
        collect information that you choose to include in your profile. This may
        include:
      </p>
      <Ul
        items={[
          "Name or display name",
          "Age",
          "Gender",
          "City or approximate location",
          "Marital status",
          "Marriage goals",
          "Relationship preferences",
          "Biography or profile description",
          "Profile photographs",
          "Education information",
          "Professional information",
          "Information about children, where voluntarily provided",
          "Partner preferences",
        ]}
      />
      <p>Some profile information may be optional.</p>

      <h2>5. Optional Religious and Cultural Information</h2>
      <p>
        Second Marriage may allow users to voluntarily provide religious,
        cultural, or compatibility-related preferences. Providing this
        information is optional.
      </p>
      <p>
        Users are not required to provide religious or cultural information to
        create an account unless a particular feature clearly requires that
        information.
      </p>
      <p>Where users voluntarily provide this information, it may be used to:</p>
      <Ul
        items={[
          "Display relevant profile information",
          "Support compatibility preferences",
          "Improve matching features",
        ]}
      />
      <p>
        Users should only provide information they are comfortable sharing
        through their profile.
      </p>

      <h2>6. Information We Do Not Require for Identity Verification</h2>
      <p>
        Second Marriage does not require users to upload government
        identification documents, passports, national identity cards, divorce
        decrees, or similar legal documents as part of the standard identity
        verification process.
      </p>
      <p>
        We encourage users not to upload highly sensitive legal or government
        documents unless we explicitly introduce a feature that clearly explains
        why such information is required.
      </p>

      <h2>7. Profile Photos and Face Verification</h2>
      <p>
        Users may upload profile photographs for their Second Marriage profile.
      </p>
      <p>
        We may offer a photo or face verification feature designed to help
        determine whether a user appears to be a real person and whether a
        verification image corresponds with the user’s profile photograph.
      </p>
      <p>
        Face verification may use machine learning technology to process images
        for the limited purpose of verification and fraud prevention.
      </p>
      <p>We do not sell biometric information.</p>
      <p>We do not use face verification information for advertising purposes.</p>
      <p>
        Verification information is used only for purposes reasonably related to
        identity verification, account integrity, safety, fraud prevention, and
        maintaining the security of the Service.
      </p>

      <h2>8. Location Information</h2>
      <p>
        We may request access to your device location when you choose to use
        location-based features. Location information may be used to:
      </p>
      <Ul
        items={[
          "Determine your approximate city or region",
          "Support location-based profile discovery",
          "Improve matching features",
        ]}
      />
      <p>
        We do not publicly display your precise geographic coordinates to other
        users.
      </p>
      <p>
        Where location services are available, users may choose not to grant
        location permission and may use alternative location options provided by
        the Service.
      </p>

      <h2>9. Messages and Communications</h2>
      <p>
        Second Marriage allows eligible matched users to communicate through the
        Service. We may process messages and communication information to:
      </p>
      <Ul
        items={[
          "Deliver messaging functionality",
          "Maintain and improve the Service",
          "Detect spam and fraud",
          "Investigate abuse and safety concerns",
          "Review reported content",
          "Enforce our Terms of Use and Community Guidelines",
          "Protect users and the integrity of the Service",
        ]}
      />
      <p>
        Users cannot send photographs through private messages unless this
        functionality is explicitly introduced in a future version of the
        Service.
      </p>
      <p>
        Users should avoid sharing highly sensitive personal, financial,
        banking, authentication, or confidential information through
        conversations.
      </p>

      <h2>10. Safety Reports and Moderation</h2>
      <p>
        Second Marriage provides safety and reporting features to help users
        report inappropriate or harmful behavior. When a user submits a report,
        we may collect information relating to:
      </p>
      <Ul
        items={[
          "The reporting user",
          "The reported user",
          "The reported content",
          "The reason for the report",
          "Relevant message or profile information",
          "Additional information voluntarily provided by the reporting user",
        ]}
      />
      <p>
        We may retain safety and moderation records where reasonably necessary
        to:
      </p>
      <Ul
        items={[
          "Investigate reports",
          "Prevent fraud",
          "Prevent abuse",
          "Protect users",
          "Enforce Community Guidelines",
          "Investigate repeated violations",
          "Comply with legal obligations",
        ]}
      />

      <h2>11. AI Profiles and AI Conversations</h2>
      <p>
        Second Marriage may include certain profiles that are generated or
        operated using artificial intelligence. These profiles are clearly
        identified with a visible “AI Profile” label.
      </p>
      <p>AI Profiles are not real individuals.</p>
      <p>
        Users should not interpret AI Profiles as genuine matrimonial
        applicants, real people, marriage proposals, or verified human matches.
      </p>
      <p>
        When a user interacts with an AI-powered profile or feature,
        conversation information may be processed by an artificial intelligence
        service provider to generate responses. AI conversation processing may
        involve information contained within the relevant conversation.
      </p>
      <p>
        AI-generated features are intended to provide conversation,
        entertainment, engagement, or other clearly disclosed functionality. We
        do not intentionally represent AI Profiles as real human users.
      </p>

      <h2>12. Device and Technical Information</h2>
      <p>
        We may automatically collect certain technical information when you use
        the Service. This may include:
      </p>
      <Ul
        items={[
          "Device type",
          "Operating system",
          "App version",
          "Device identifiers",
          "IP address or network information where necessary",
          "Diagnostic information",
          "Crash reports",
          "Security-related information",
        ]}
      />
      <p>We use this information to:</p>
      <Ul
        items={[
          "Operate the Service",
          "Diagnose technical problems",
          "Improve performance",
          "Improve reliability",
          "Detect security issues",
          "Prevent fraud and abuse",
        ]}
      />

      <h2>13. Analytics Information</h2>
      <p>
        We may use analytics services to understand how users interact with
        Second Marriage. Analytics information may include:
      </p>
      <Ul
        items={[
          "App usage",
          "Feature interactions",
          "Screen views",
          "Account activity",
          "Onboarding activity",
          "Matching activity",
          "Subscription activity",
          "General engagement information",
        ]}
      />
      <p>We use analytics information to:</p>
      <Ul
        items={[
          "Improve the Service",
          "Understand feature performance",
          "Improve user experience",
          "Identify technical issues",
          "Improve product development",
        ]}
      />

      <h2>14. Advertising</h2>
      <p>
        Second Marriage may display advertising or use advertising-related
        technologies where applicable. Advertising technologies may process
        limited technical or device information to:
      </p>
      <Ul
        items={[
          "Display advertising",
          "Measure advertising performance",
          "Prevent advertising fraud",
          "Improve advertising services",
        ]}
      />
      <p>
        Where required by applicable law or platform requirements, we will
        obtain appropriate consent before using information for personalized
        advertising or tracking.
      </p>
      <p>
        Users with an eligible premium subscription may receive an ad-free
        experience where offered.
      </p>

      <h2>15. Subscriptions and Payments</h2>
      <p>Second Marriage may offer premium subscriptions and digital features.</p>
      <p>
        Payments are processed through authorized platform payment systems and
        payment providers, such as Apple or Google.
      </p>
      <p>
        We do not directly collect or store complete payment card numbers when
        payments are processed through platform payment systems.
      </p>
      <p>We may process subscription-related information to:</p>
      <Ul
        items={[
          "Confirm subscription status",
          "Provide premium features",
          "Restore purchases",
          "Prevent fraud",
          "Provide customer support",
        ]}
      />

      <h2>16. How We Use Your Information</h2>
      <p>We may use collected information to:</p>
      <Ul
        items={[
          "Create and manage accounts",
          "Provide matrimonial and matching features",
          "Display user profiles",
          "Enable communication between users",
          "Provide profile discovery",
          "Support compatibility preferences",
          "Improve matching functionality",
          "Verify accounts",
          "Reduce fake profiles",
          "Prevent fraud and abuse",
          "Provide customer support",
          "Process subscriptions",
          "Send service-related notifications",
          "Improve product functionality",
          "Improve user experience",
          "Enforce our Terms of Use",
          "Enforce Community Guidelines",
          "Protect users and the Service",
          "Comply with applicable legal obligations",
        ]}
      />

      <h2>17. How Information May Be Shared</h2>
      <p>We may share information in the following circumstances.</p>
      <h3>With Other Users</h3>
      <p>
        Information included in your profile may be visible to other users
        according to your privacy and profile visibility settings.
      </p>
      <h3>With Service Providers</h3>
      <p>
        We may use third-party service providers to support services such as:
      </p>
      <Ul
        items={[
          "Authentication",
          "Cloud infrastructure",
          "Database services",
          "File storage",
          "Analytics",
          "Crash reporting",
          "Notifications",
          "Subscription management",
          "Advertising",
          "Artificial intelligence functionality",
          "Security and fraud prevention",
        ]}
      />
      <p>
        Service providers may process information as necessary to provide
        services to us.
      </p>
      <h3>For Legal and Safety Reasons</h3>
      <p>We may disclose information where reasonably necessary to:</p>
      <Ul
        items={[
          "Comply with applicable laws",
          "Respond to valid legal requests",
          "Protect users",
          "Investigate fraud",
          "Investigate abuse",
          "Enforce our Terms and Community Guidelines",
          "Protect the rights, safety, and security of Second Marriage and others",
        ]}
      />

      <h2>18. Profile Privacy Controls</h2>
      <p>
        Second Marriage may provide privacy controls that allow users to manage
        certain aspects of profile visibility. Depending on available features,
        users may be able to control:
      </p>
      <Ul
        items={[
          "Profile visibility",
          "Photo visibility",
          "Phone number search settings",
          "Activity visibility",
          "Anonymous browsing features",
          "Other available privacy settings",
        ]}
      />
      <p>
        Users should review their privacy settings and select the options
        appropriate for their personal preferences.
      </p>

      <h2>19. Data Retention</h2>
      <p>
        We retain personal information only for as long as reasonably necessary
        to:
      </p>
      <Ul
        items={[
          "Provide the Service",
          "Maintain user accounts",
          "Protect users",
          "Prevent fraud and abuse",
          "Investigate safety reports",
          "Resolve disputes",
          "Enforce our agreements",
          "Meet legal obligations",
        ]}
      />
      <p>
        Retention periods may vary depending on the type of information and the
        purpose for which it was collected.
      </p>

      <h2>20. Account and Data Deletion</h2>
      <p>
        Users may request deletion of their Second Marriage account through the
        account deletion feature available within the application.
      </p>
      <p>
        When an account deletion request is completed, we will delete or
        anonymize personal information associated with the account in accordance
        with our applicable deletion procedures.
      </p>
      <p>
        Certain limited information may be retained where necessary for
        legitimate purposes, including:
      </p>
      <Ul
        items={[
          "Fraud prevention",
          "Security",
          "Abuse investigations",
          "Legal compliance",
          "Dispute resolution",
        ]}
      />
      <p>
        Any retained information will be kept only for as long as reasonably
        necessary for the applicable purpose.
      </p>
      <p>
        Deleting your Second Marriage account does not automatically cancel
        subscriptions purchased through Apple, Google, or another applicable
        platform. Subscription cancellation must be managed through the relevant
        payment platform.
      </p>

      <h2>21. Data Security</h2>
      <p>
        We use reasonable technical and organizational measures designed to
        protect personal information. However, no electronic transmission,
        storage system, or internet-based service can be guaranteed to be
        completely secure.
      </p>
      <p>
        Users should take reasonable steps to protect their accounts and
        personal information. We recommend that users:
      </p>
      <Ul
        items={[
          "Use strong passwords",
          "Do not share account credentials",
          "Do not send money to individuals they only know online",
          "Do not share banking information",
          "Do not share passwords or verification codes",
          "Report suspicious activity",
        ]}
      />

      <h2>22. Children’s Privacy</h2>
      <p>
        Second Marriage is intended only for individuals aged 18 and above. We
        do not knowingly permit individuals under the age of 18 to use the
        Service.
      </p>
      <p>
        If we discover that an account belongs to an underage user, we may
        suspend or remove the account and associated personal information.
      </p>

      <h2>23. International Data Processing</h2>
      <p>
        Second Marriage may use cloud infrastructure and service providers
        located in countries other than the country where you live. Your
        information may therefore be processed in locations where our service
        providers operate.
      </p>
      <p>
        Where required by applicable law, we will implement appropriate
        safeguards for international transfers of personal information.
      </p>

      <h2>24. Your Privacy Rights</h2>
      <p>
        Depending on your location and applicable privacy laws, you may have
        rights relating to your personal information. These rights may include
        the ability to:
      </p>
      <Ul
        items={[
          "Request access to certain personal information",
          "Request correction of inaccurate information",
          "Request deletion of eligible information",
          "Withdraw consent where applicable",
          "Object to certain processing activities",
          "Contact us regarding privacy concerns",
        ]}
      />
      <p>
        To exercise applicable rights, contact us using the information provided
        below.
      </p>

      <h2>25. Third-Party Services</h2>
      <p>
        Second Marriage may use third-party services and software development
        kits to provide features including:
      </p>
      <Ul
        items={[
          "Authentication",
          "Cloud infrastructure",
          "Analytics",
          "Crash reporting",
          "Notifications",
          "Subscription management",
          "Advertising",
          "Artificial intelligence",
          "Security",
        ]}
      />
      <p>
        Third-party services may process information according to their
        applicable privacy policies and contractual obligations.
      </p>

      <h2>26. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. If we make material
        changes, we may provide notice through the application, website, email,
        or another appropriate communication method.
      </p>
      <p>
        The “Last Updated” date at the beginning of this Privacy Policy
        indicates when the Policy was most recently updated.
      </p>

      <h2>27. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or how Second Marriage
        handles personal information, please contact us:
      </p>
      {/* TODO: add company legal name and privacy email once confirmed */}
      <div className="sm-contact">
        <p>
          <strong>App Name:</strong> Second Marriage
        </p>
        <p>
          <strong>Website:</strong>{" "}
          <a href="https://macromatrix.io/">https://macromatrix.io/</a>
        </p>
        <p>
          <strong>Country:</strong> Pakistan
        </p>
      </div>
    </div>
  );
}

export function SecondMarriageTerms() {
  return (
    <div className="sm-legal">
      <SEO title="Terms of Service – Second Marriage" description="Terms and conditions for using the Second Marriage matrimonial app and related services." path="/secondmarriage/termsofservices" />
      <h1>Terms of Service for Second Marriage</h1>
      <p className="sm-meta">Effective Date: 3 September 2026 · Last Updated: 3 September 2026</p>

      <h2>1. Acceptance of These Terms</h2>
      <p>Welcome to Second Marriage.</p>
      <p>These Terms of Service ("Terms") govern your access to and use of the Second Marriage mobile application, website, and related services (collectively, the "Service").</p>
      <p>By downloading, accessing, creating an account, or using Second Marriage, you agree to be bound by these Terms and our Privacy Policy.</p>
      <p>If you do not agree with these Terms, you must not use the Service.</p>

      <h2>2. About Second Marriage</h2>
      <p>Second Marriage is a matrimonial and relationship platform designed to help eligible adults discover, connect with, and communicate with potential partners for meaningful relationships and marriage.</p>
      <p>Second Marriage provides technology that enables users to create profiles, discover other profiles, express interest, communicate with matched users, and use related matrimonial features.</p>
      <p>Second Marriage does not:</p>
      <ul>
        <li>Arrange or guarantee marriages</li>
        <li>Guarantee that you will find a spouse or partner</li>
        <li>Guarantee compatibility between users</li>
        <li>Guarantee that any user will respond to you</li>
        <li>Guarantee that profiles are suitable for you</li>
        <li>Guarantee the conduct, intentions, identity, or information provided by other users</li>
      </ul>
      <p>Users are responsible for exercising their own judgment when interacting with other users.</p>

      <h2>3. Eligibility</h2>
      <p>To use Second Marriage, you must:</p>
      <ul>
        <li>Be at least 18 years old</li>
        <li>Be legally permitted to enter into a relationship or marriage under applicable laws</li>
        <li>Have the legal capacity to enter into these Terms</li>
        <li>Provide accurate information about yourself</li>
        <li>Use the Service in accordance with these Terms and applicable laws</li>
      </ul>
      <p>You must not create or maintain an account if:</p>
      <ul>
        <li>You are under 18 years old</li>
        <li>You have been permanently banned from Second Marriage</li>
        <li>You are prohibited from using the Service under applicable law</li>
        <li>You intend to use the Service for unlawful, fraudulent, commercial, or harmful purposes</li>
      </ul>
      <p>We may request additional information or verification where reasonably necessary to protect the Service or its users.</p>

      <h2>4. Account Registration</h2>
      <p>To access certain features, you may be required to create an account.</p>
      <p>You agree to:</p>
      <ul>
        <li>Provide accurate and truthful information</li>
        <li>Keep your account information reasonably up to date</li>
        <li>Protect your login credentials</li>
        <li>Not share your account with another person</li>
        <li>Notify us of suspected unauthorized access where applicable</li>
      </ul>
      <p>You are responsible for activities conducted through your account. You must not create an account using another person's identity or impersonate another person.</p>

      <h2>5. Profile Information</h2>
      <p>Second Marriage allows users to create matrimonial profiles. You are responsible for the information you provide in your profile.</p>
      <p>You agree that you will not:</p>
      <ul>
        <li>Provide false or misleading information</li>
        <li>Use another person's photographs without permission</li>
        <li>Impersonate another person</li>
        <li>Misrepresent your age</li>
        <li>Misrepresent your marital status</li>
        <li>Misrepresent your identity</li>
        <li>Create fake profiles</li>
        <li>Provide fraudulent information to deceive other users</li>
      </ul>
      <p>Some profile information may be visible to other users according to your privacy settings and the functionality of the Service. You should carefully consider what information you choose to share publicly or with other users.</p>

      <h2>6. Profile Verification</h2>
      <p>Second Marriage may provide photo or face verification features designed to help improve account integrity and reduce fake profiles.</p>
      <p>Verification is intended to provide an additional safety signal and does not guarantee that a user is completely safe, trustworthy, compatible, legally eligible for marriage, or accurately represented in every aspect.</p>
      <p>A verification status must not be interpreted as a guarantee or endorsement by Second Marriage.</p>
      <p>We may suspend, remove, or review verification status where we reasonably believe verification information is inaccurate, misleading, fraudulent, or otherwise violates these Terms.</p>

      <h2>7. Matching and Communication</h2>
      <p>Second Marriage may allow users to:</p>
      <ul>
        <li>Discover other profiles</li>
        <li>Express interest</li>
        <li>Accept or decline interest requests</li>
        <li>Create matches</li>
        <li>Communicate with matched users</li>
        <li>Unmatch users</li>
        <li>Block users</li>
        <li>Report inappropriate behavior</li>
      </ul>
      <p>You remain responsible for your interactions with other users. Second Marriage does not control every interaction between users and cannot guarantee the behavior of users online or offline. You should exercise appropriate caution when communicating with or meeting another person.</p>

      <h2>8. Safety and Offline Meetings</h2>
      <p>Users are solely responsible for decisions regarding offline meetings and interactions. Before meeting another user, we encourage you to take reasonable precautions, including:</p>
      <ul>
        <li>Communicating carefully before meeting</li>
        <li>Meeting initially in a public place</li>
        <li>Informing a trusted person about your plans</li>
        <li>Avoiding sharing unnecessary financial information</li>
        <li>Never sending money to someone you have only met online</li>
        <li>Being cautious when another user requests financial assistance</li>
        <li>Reporting suspicious activity</li>
      </ul>
      <p>Second Marriage does not conduct background checks on every user unless explicitly stated otherwise. You are responsible for determining whether another person is appropriate for you.</p>

      <h2>9. Prohibited Conduct</h2>
      <p>You must not use Second Marriage to:</p>
      <ul>
        <li>Break applicable laws</li>
        <li>Commit fraud</li>
        <li>Scam or deceive users</li>
        <li>Request money fraudulently</li>
        <li>Engage in financial scams</li>
        <li>Impersonate another person</li>
        <li>Create fake profiles</li>
        <li>Harass or threaten another person</li>
        <li>Bully another person</li>
        <li>Stalk another person</li>
        <li>Send spam</li>
        <li>Send malicious links</li>
        <li>Attempt to obtain passwords or financial information</li>
        <li>Promote illegal activity</li>
        <li>Exploit or manipulate another user</li>
        <li>Engage in hate speech or discriminatory harassment</li>
        <li>Share sexually explicit content where prohibited by our rules</li>
        <li>Upload unlawful or harmful content</li>
        <li>Attempt to involve minors in adult relationship activities</li>
        <li>Collect information about other users without authorization</li>
        <li>Sell or commercially exploit user information</li>
        <li>Use automated systems to access the Service without authorization</li>
        <li>Attempt to interfere with the security or operation of the Service</li>
      </ul>
      <p>We may investigate suspected violations and take appropriate action.</p>

      <h2>10. No Minors</h2>
      <p>Second Marriage is strictly intended for adults aged 18 and above. You must not:</p>
      <ul>
        <li>Create an account if you are under 18</li>
        <li>Misrepresent your age</li>
        <li>Use the Service to communicate with minors for romantic or matrimonial purposes</li>
        <li>Attempt to involve minors in adult relationship activities</li>
      </ul>
      <p>If we believe an account belongs to a person under 18, we may suspend or remove the account. We may also take further action where required by applicable law or necessary to protect users.</p>

      <h2>11. User Content</h2>
      <p>Users may create and share content through Second Marriage, including profile information, profile photographs, biographies, messages, marriage preferences, and other permitted profile information.</p>
      <p>You remain responsible for the content you submit.</p>
      <p>By submitting content to Second Marriage, you grant us a limited, non-exclusive, worldwide, royalty-free license to host, store, process, reproduce, display, and use that content only as reasonably necessary to operate, maintain, secure, and improve the Service.</p>
      <p>You retain ownership of your content, subject to the rights necessary for us to provide the Service.</p>

      <h2>12. Content Standards</h2>
      <p>You must not submit content that:</p>
      <ul>
        <li>Is fraudulent or deceptive</li>
        <li>Violates another person's rights</li>
        <li>Contains unlawful material</li>
        <li>Contains sexually explicit material where prohibited</li>
        <li>Exploits minors</li>
        <li>Promotes violence or serious harm</li>
        <li>Contains hateful or discriminatory harassment</li>
        <li>Contains malicious software</li>
        <li>Contains scams or fraudulent schemes</li>
        <li>Contains another person's private information without authorization</li>
        <li>Uses stolen photographs</li>
        <li>Is intended to deceive users about your identity</li>
      </ul>
      <p>We may remove content that violates these Terms or our Community Guidelines.</p>

      <h2>13. Reporting and Blocking</h2>
      <p>Second Marriage provides safety tools that may allow users to block other users, report profiles, report messages, report inappropriate content, and unmatch users.</p>
      <p>Users should use reporting tools responsibly. Submitting intentionally false or malicious reports may itself violate these Terms.</p>
      <p>We may review reports and take action at our discretion based on available information. Possible actions may include:</p>
      <ul>
        <li>Warning a user</li>
        <li>Removing content</li>
        <li>Restricting features</li>
        <li>Suspending an account</li>
        <li>Permanently terminating an account</li>
      </ul>
      <p>We are not required to disclose details of moderation actions involving another user.</p>

      <h2>14. AI Profiles</h2>
      <p>Second Marriage may include certain profiles or features generated or operated using artificial intelligence. AI-operated profiles are clearly identified with a visible "AI Profile" label. AI Profiles are not real human users.</p>
      <p>You acknowledge and understand that:</p>
      <ul>
        <li>AI Profiles are not real individuals</li>
        <li>AI Profiles are not genuine matrimonial applicants</li>
        <li>AI Profiles are not real marriage proposals</li>
        <li>AI Profiles should not be interpreted as verified human users</li>
        <li>Conversations with AI Profiles may be generated using artificial intelligence</li>
      </ul>
      <p>AI Profiles may be provided for clearly disclosed purposes such as conversation, entertainment, engagement, demonstration, or other app functionality.</p>
      <p>You should not make important personal, financial, relationship, or marriage decisions based solely on information provided by an AI Profile.</p>

      <h2>15. AI Conversations</h2>
      <p>When you interact with an AI-powered feature, information from the relevant conversation may be processed by artificial intelligence systems to generate responses.</p>
      <p>You should avoid providing highly sensitive information to AI-powered features, including passwords, banking information, payment card information, government identification numbers, and authentication codes.</p>
      <p>AI-generated responses may contain inaccuracies or limitations. You remain responsible for decisions you make based on AI-generated content.</p>

      <h2>16. Privacy</h2>
      <p>Your use of Second Marriage is also governed by our <NavLink to="/secondmarriage/privacypolicy">Privacy Policy</NavLink>. Our Privacy Policy explains how we collect, use, store, share, and protect personal information.</p>

      <h2>17. Premium Subscriptions</h2>
      <p>Second Marriage may offer premium subscriptions and paid features. Premium features may include benefits such as:</p>
      <ul>
        <li>Additional profile discovery features</li>
        <li>Enhanced matching tools</li>
        <li>Increased communication limits</li>
        <li>Advanced filters</li>
        <li>Profile visibility features</li>
        <li>Profile boosts</li>
        <li>Other premium functionality</li>
      </ul>
      <p>Available features may vary by location, platform, subscription plan, and app version.</p>

      <h2>18. Payments</h2>
      <p>Payments for subscriptions or digital features may be processed through authorized platform payment systems, including Apple App Store or Google Play. Payment terms, billing, renewal, cancellation, refunds, and subscription management may also be governed by the applicable platform's rules. Second Marriage does not control payment processing performed by Apple, Google, or other authorized payment providers.</p>

      <h2>19. Automatic Renewal</h2>
      <p>Where permitted by the applicable platform and applicable law, subscriptions may automatically renew unless cancelled before the applicable renewal date. The applicable subscription price and billing period will be displayed before purchase. You are responsible for managing your subscription through the applicable platform account.</p>

      <h2>20. Cancellation</h2>
      <p>Subscription cancellation must generally be completed through the platform where the subscription was purchased. Deleting your Second Marriage account does not automatically cancel an active subscription.</p>
      <p>Users should cancel subscriptions through:</p>
      <ul>
        <li>Apple App Store subscription settings for Apple purchases</li>
        <li>Google Play subscription settings for Google Play purchases</li>
      </ul>
      <p>Cancellation may take effect at the end of the current billing period according to the applicable platform's policies.</p>

      <h2>21. Refunds</h2>
      <p>Refund eligibility and processing may depend on the platform through which the purchase was made, applicable consumer protection laws, and the terms and policies of the payment provider. Second Marriage does not guarantee refunds except where required by applicable law or applicable platform policies.</p>

      <h2>22. Advertising</h2>
      <p>Second Marriage may display advertising to eligible users. Advertising availability and formats may change over time. Certain premium subscriptions may include an ad-free experience where specifically stated. Advertising technologies may process limited technical information as described in our Privacy Policy.</p>

      <h2>23. Account Suspension and Termination</h2>
      <p>We may suspend, restrict, or terminate your account if we reasonably believe that:</p>
      <ul>
        <li>You violated these Terms</li>
        <li>You violated our Community Guidelines</li>
        <li>You provided fraudulent information</li>
        <li>You created a fake profile</li>
        <li>You impersonated another person</li>
        <li>You engaged in fraud or scams</li>
        <li>You harassed another user</li>
        <li>You created safety risks</li>
        <li>You attempted to involve minors in prohibited activities</li>
        <li>You violated applicable laws</li>
        <li>Your account created a significant risk to other users or the Service</li>
      </ul>
      <p>We may take action without prior notice where necessary to protect users, investigate abuse, or comply with legal obligations.</p>

      <h2>24. Appeals</h2>
      <p>Where an appeal feature is available, users may submit an appeal regarding certain moderation or account decisions. Submitting an appeal does not guarantee reinstatement. We may review available information and make a decision based on our applicable policies and safety considerations. Our decision may be final where permitted by applicable law.</p>

      <h2>25. Account Deletion</h2>
      <p>Users may request deletion of their Second Marriage account through the account deletion feature available in the application. Account deletion may result in the removal or anonymization of information associated with your account, subject to legitimate retention requirements. Certain information may be retained where reasonably necessary for fraud prevention, security, abuse investigations, legal obligations, and dispute resolution.</p>
      <p>Deleting your account does not automatically cancel active subscriptions purchased through Apple, Google, or another authorized payment platform.</p>

      <h2>26. Intellectual Property</h2>
      <p>Second Marriage and its associated software, branding, logos, designs, text, graphics, and technology are owned by or licensed to Second Marriage and are protected by applicable intellectual property laws.</p>
      <p>You may not:</p>
      <ul>
        <li>Copy the Service</li>
        <li>Reverse engineer the Service except where legally permitted</li>
        <li>Reproduce our branding without permission</li>
        <li>Use our intellectual property for unauthorized commercial purposes</li>
        <li>Attempt to extract source code except where applicable law expressly permits it</li>
      </ul>

      <h2>27. Third-Party Services</h2>
      <p>The Service may use third-party services and technologies. We are not responsible for third-party websites or services that are not operated by Second Marriage. Your use of third-party services may be subject to separate terms and privacy policies.</p>

      <h2>28. Service Availability</h2>
      <p>We aim to provide a reliable Service, but we do not guarantee that Second Marriage will always be available, error-free, uninterrupted, secure from all threats, or compatible with every device. We may modify, suspend, or discontinue features where reasonably necessary.</p>

      <h2>29. Disclaimer Regarding Other Users</h2>
      <p>Second Marriage does not guarantee the identity, accuracy, marital status, intentions, or behavior of any other user, the safety of offline interactions, relationship compatibility, or marriage success. Users must use independent judgment when interacting with others.</p>

      <h2>30. No Guarantee of Marriage or Relationship Success</h2>
      <p>Second Marriage is a technology platform that helps users discover and communicate with potential partners. We do not guarantee marriage, engagement, a successful relationship, compatibility, any particular number of matches, responses from other users, or relationship outcomes. Individual results will vary.</p>

      <h2>31. Limitation of Liability</h2>
      <p>To the maximum extent permitted by applicable law, Second Marriage and its owners, affiliates, employees, contractors, and service providers will not be liable for indirect, incidental, special, consequential, or punitive damages arising from use of the Service, interactions with other users, offline meetings, user-generated content, AI-generated content, loss of data, service interruptions, unauthorized access, or actions of third parties.</p>
      <p>Nothing in these Terms excludes liability that cannot legally be excluded under applicable law.</p>

      <h2>32. Indemnification</h2>
      <p>To the extent permitted by applicable law, you agree to indemnify and hold harmless Second Marriage and its affiliates, officers, employees, and service providers from claims, damages, liabilities, and expenses arising from your violation of these Terms, your violation of applicable laws, your misuse of the Service, your interaction with another user, and content you submit through the Service.</p>

      <h2>33. Changes to the Service</h2>
      <p>We may modify, improve, add, remove, or discontinue features of Second Marriage from time to time. We may also modify eligibility requirements, subscription features, safety systems, or technical functionality where reasonably necessary.</p>

      <h2>34. Changes to These Terms</h2>
      <p>We may update these Terms from time to time. If we make material changes, we may provide notice through the Second Marriage application, email, our website, or another appropriate communication method. Your continued use of the Service after updated Terms become effective may constitute acceptance where permitted by applicable law.</p>

      <h2>35. Governing Law</h2>
      <p>These Terms shall be governed by the laws of the Islamic Republic of Pakistan. This section does not limit any mandatory consumer protection rights that apply to you under applicable law.</p>

      <h2>36. Contact Us</h2>
      <p>If you have questions regarding these Terms of Service, please contact us:</p>
      <div className="sm-contact">
        <p><strong>App Name:</strong> Second Marriage</p>
        <p><strong>Company:</strong> MacroMatrix Pvt Limited</p>
        <p><strong>Email:</strong> <a href="mailto:support@macromatrix.io">support@macromatrix.io</a></p>
        <p><strong>Website:</strong> <a href="https://macromatrix.io/">https://macromatrix.io/</a></p>
      </div>
    </div>
  );
}

export function SecondMarriageEula() {
  return (
    <div className="sm-legal">
      <SEO title="EULA – Second Marriage" description="End User License Agreement for the Second Marriage matrimonial app by MacroMatrix." path="/secondmarriage/eula" />
      <h1>End User License Agreement (EULA)</h1>
      <p className="sm-meta">Effective Date: 3 September 2026 · Last Updated: 3 September 2026</p>

      <h2>1. Introduction</h2>
      <p>This End User License Agreement ("EULA") is a legal agreement between you ("User," "you," or "your") and Macro Matrix Pvt Limited ("Second Marriage," "Company," "we," "us," or "our").</p>
      <p>This EULA governs your download, installation, access to, and use of the Second Marriage mobile application, including any updates, features, content, software, and related services provided through the application (collectively, the "Application").</p>
      <p>By downloading, installing, accessing, or using the Application, you agree to be bound by this EULA. If you do not agree to this EULA, you must not download, install, access, or use the Application.</p>

      <h2>2. License Grant</h2>
      <p>Subject to your compliance with this EULA, we grant you a limited, personal, non-exclusive, non-transferable, non-sublicensable, revocable license to:</p>
      <ul>
        <li>Download the Application</li>
        <li>Install the Application on a compatible device</li>
        <li>Access and use the Application for personal and non-commercial purposes</li>
        <li>Use the Application only in accordance with this EULA, our Terms of Service, Community Guidelines, Privacy Policy, and applicable laws</li>
      </ul>
      <p>This license does not transfer ownership of the Application or any intellectual property rights to you.</p>

      <h2>3. License Restrictions</h2>
      <p>You may not, and may not permit another person to:</p>
      <ul>
        <li>Copy, reproduce, distribute, or commercially exploit the Application</li>
        <li>Sell, rent, lease, sublicense, or transfer the Application</li>
        <li>Modify or create derivative works based on the Application</li>
        <li>Reverse engineer, decompile, disassemble, or attempt to discover source code except where expressly permitted by applicable law</li>
        <li>Circumvent security features or technical protections</li>
        <li>Use automated systems, bots, scrapers, or similar technologies without authorization</li>
        <li>Interfere with the functionality or security of the Application</li>
        <li>Attempt unauthorized access to accounts, systems, databases, or infrastructure</li>
        <li>Use the Application for unlawful purposes</li>
        <li>Use the Application in violation of our Terms of Service or Community Guidelines</li>
      </ul>

      <h2>4. Eligibility</h2>
      <p>The Application is intended only for individuals who are at least 18 years old. By using the Application, you represent and warrant that:</p>
      <ul>
        <li>You are at least 18 years old</li>
        <li>You are legally permitted to use the Application</li>
        <li>You have the legal capacity to enter into this EULA</li>
        <li>You are not prohibited from using the Application under applicable law</li>
        <li>You will comply with applicable laws and regulations</li>
      </ul>
      <p>Individuals under the age of 18 are not permitted to use the Application.</p>

      <h2>5. Purpose of the Application</h2>
      <p>Second Marriage is a matrimonial and relationship platform designed to help eligible adults discover and communicate with potential partners for meaningful relationships and marriage.</p>
      <p>The Application is a technology platform. We do not guarantee marriage, engagement, a successful relationship, compatibility, a specific number of matches, responses from other users, the intentions of other users, the accuracy of user profiles, the identity or marital status of every user, or the outcome of any relationship or offline interaction.</p>
      <p>Users are responsible for their own decisions and interactions.</p>

      <h2>6. User Accounts</h2>
      <p>Certain features require you to create an account. You agree to:</p>
      <ul>
        <li>Provide accurate information</li>
        <li>Maintain reasonably accurate account information</li>
        <li>Protect your login credentials</li>
        <li>Not share your account with another person</li>
        <li>Not impersonate another person</li>
        <li>Not create fraudulent or misleading accounts</li>
      </ul>
      <p>You are responsible for activity conducted through your account. We may suspend or terminate accounts that violate this EULA, our Terms of Service, Community Guidelines, or applicable law.</p>

      <h2>7. User Content</h2>
      <p>The Application may allow you to submit or create content, including profile information, profile photographs, biographies, messages, preferences, and other permitted user-generated content.</p>
      <p>You retain ownership of content you submit, subject to the license necessary for us to operate the Application.</p>
      <p>By submitting content through the Application, you grant us a limited, non-exclusive, worldwide, royalty-free license to host, store, reproduce, process, display, and use your content solely as reasonably necessary to operate, maintain, secure, and improve the Application.</p>
      <p>You are responsible for ensuring that you have the right to submit any content you upload.</p>

      <h2>8. User Conduct</h2>
      <p>You must not use the Application to:</p>
      <ul>
        <li>Commit fraud</li>
        <li>Scam or deceive another user</li>
        <li>Request money through fraudulent or deceptive means</li>
        <li>Impersonate another person</li>
        <li>Create fake profiles</li>
        <li>Harass, threaten, or stalk another person</li>
        <li>Send spam</li>
        <li>Distribute malicious links or software</li>
        <li>Obtain another person's private information without authorization</li>
        <li>Promote unlawful activity</li>
        <li>Exploit or manipulate users</li>
        <li>Engage in prohibited sexual or explicit content</li>
        <li>Engage in hateful or discriminatory harassment</li>
        <li>Involve or attempt to involve minors in adult relationship activities</li>
        <li>Violate another person's legal rights</li>
        <li>Interfere with the safety or operation of the Application</li>
      </ul>

      <h2>9. User Safety and Offline Meetings</h2>
      <p>Second Marriage does not control interactions between users outside the Application. If you choose to communicate with or meet another user, you do so at your own discretion and risk.</p>
      <p>We encourage users to:</p>
      <ul>
        <li>Exercise caution when communicating with strangers</li>
        <li>Meet initially in public places</li>
        <li>Inform a trusted person before an offline meeting</li>
        <li>Avoid sharing financial information</li>
        <li>Never send money to someone based solely on an online relationship</li>
        <li>Report suspicious behavior</li>
      </ul>
      <p>You are responsible for your decisions regarding interactions with other users.</p>

      <h2>10. Profile and Face Verification</h2>
      <p>The Application may provide photo or face verification features intended to help improve account integrity and reduce fake profiles.</p>
      <p>A verification status is not a guarantee of identity, safety, trustworthiness, marital eligibility, or an endorsement by Second Marriage.</p>
      <p>We may review, remove, restrict, or revoke verification status where reasonably necessary to protect users or the Application.</p>

      <h2>11. AI Profiles</h2>
      <p>The Application may contain certain profiles or conversations generated or operated using artificial intelligence. AI-operated profiles are clearly identified with a visible "AI Profile" label.</p>
      <p>You acknowledge and understand that:</p>
      <ul>
        <li>AI Profiles are not real people</li>
        <li>AI Profiles are not genuine matrimonial applicants</li>
        <li>AI Profiles are not verified human users</li>
        <li>AI Profiles are not genuine marriage proposals</li>
        <li>AI-generated responses may contain inaccuracies</li>
      </ul>
      <p>You should not make financial, legal, personal, relationship, or marriage decisions based solely on information provided by an AI Profile.</p>

      <h2>12. Artificial Intelligence Features</h2>
      <p>Certain features may use artificial intelligence technologies. AI-generated responses may be inaccurate, incomplete, contextually incorrect, outdated, or generated automatically. You are responsible for evaluating information generated through AI features.</p>
      <p>Do not provide highly sensitive information to AI features, including passwords, authentication codes, banking information, payment card information, or government identification numbers.</p>

      <h2>13. Intellectual Property</h2>
      <p>The Application, including its software, source code, user interface, design, logos, branding, graphics, text, features, and technology, is owned by or licensed to Second Marriage and is protected by applicable intellectual property laws.</p>
      <p>Except for the limited license granted under this EULA, no ownership rights are transferred to you.</p>

      <h2>14. Third-Party Services</h2>
      <p>The Application may integrate third-party services, including services for authentication, cloud infrastructure, analytics, notifications, artificial intelligence, advertising, payments, subscription management, and security. Your use of third-party services may be governed by separate terms and privacy policies. We are not responsible for third-party services that are outside our reasonable control.</p>

      <h2>15. Updates and Changes</h2>
      <p>We may provide updates, upgrades, patches, modifications, or new versions of the Application that may add new features, modify existing features, remove features, improve security, or fix technical issues. Certain updates may be required to continue using the Application. This EULA applies to updates unless separate terms are provided.</p>

      <h2>16. Subscription Features</h2>
      <p>The Application may provide paid subscriptions or premium features. Subscription features may vary based on platform, country, app version, and subscription plan. Purchases may be processed through Apple App Store, Google Play, or another authorized payment provider.</p>

      <h2>17. Termination of License</h2>
      <p>This license remains effective until terminated. We may suspend or terminate your license and access to the Application if:</p>
      <ul>
        <li>You violate this EULA</li>
        <li>You violate our Terms of Service</li>
        <li>You violate our Community Guidelines</li>
        <li>You engage in fraud or scams</li>
        <li>You create safety risks</li>
        <li>You create fake profiles</li>
        <li>You violate applicable law</li>
        <li>Your continued access may reasonably harm users or the Application</li>
      </ul>
      <p>Upon termination, you must stop using the Application.</p>

      <h2>18. Disclaimer of Warranties</h2>
      <p>To the maximum extent permitted by applicable law, the Application is provided on an "AS IS" and "AS AVAILABLE" basis. We do not guarantee that the Application will always be available, be uninterrupted, be error-free, be completely secure, meet every user's expectations, or produce successful matches or relationships.</p>
      <p>Nothing in this EULA limits warranties or consumer rights that cannot legally be excluded.</p>

      <h2>19. Limitation of Liability</h2>
      <p>To the maximum extent permitted by applicable law, Second Marriage and its owners, affiliates, employees, contractors, and service providers will not be liable for indirect, incidental, consequential, special, or punitive damages arising from your use of the Application, interactions with other users, offline meetings, user-generated content, AI-generated content, loss of data, service interruptions, third-party services, or unauthorized actions of other users.</p>
      <p>Nothing in this section excludes liability that cannot legally be excluded under applicable law.</p>

      <h2>20. Indemnification</h2>
      <p>To the extent permitted by applicable law, you agree to indemnify and hold harmless Second Marriage and its affiliates, employees, officers, and service providers from claims, damages, liabilities, and expenses resulting from your violation of this EULA, your misuse of the Application, your violation of applicable law, your violation of another person's rights, content submitted by you, and your interactions with other users.</p>

      <h2>21. Privacy</h2>
      <p>Your use of the Application is subject to our <NavLink to="/secondmarriage/privacypolicy">Privacy Policy</NavLink>. The Privacy Policy explains how personal information may be collected, used, stored, and shared. You should review the Privacy Policy before using the Application.</p>

      <h2>22. Account Deletion</h2>
      <p>You may request deletion of your account through the account deletion feature available within the Application. Account deletion may result in deletion or anonymization of information associated with your account, subject to legitimate retention requirements for security, fraud prevention, abuse investigations, legal compliance, and dispute resolution.</p>
      <p>Deleting your account does not automatically cancel subscriptions purchased through Apple App Store, Google Play, or another payment platform.</p>

      <h2>23. Changes to This EULA</h2>
      <p>We may update this EULA from time to time. If we make material changes, we may provide notice through the Application, our website, email, or another appropriate communication method. Your continued use of the Application after updated terms become effective may constitute acceptance where permitted by applicable law.</p>

      <h2>24. Governing Law</h2>
      <p>This EULA shall be governed by the laws of the Islamic Republic of Pakistan. This section does not limit any mandatory consumer protection rights that apply to you under applicable law.</p>

      <h2>25. Severability</h2>
      <p>If any provision of this EULA is found to be invalid, unlawful, or unenforceable, the remaining provisions will remain in effect to the maximum extent permitted by applicable law.</p>

      <h2>26. Entire Agreement</h2>
      <p>This EULA, together with the applicable Terms of Service, Privacy Policy, and Community Guidelines, constitutes the agreement between you and Second Marriage regarding your use of the Application. If there is a conflict between documents, applicable mandatory laws and platform requirements will apply.</p>

      <h2>27. Contact Information</h2>
      <p>If you have questions regarding this EULA, please contact:</p>
      <div className="sm-contact">
        <p><strong>App Name:</strong> Second Marriage</p>
        <p><strong>Company:</strong> Macro Matrix Pvt Limited</p>
        <p><strong>Legal Email:</strong> <a href="mailto:contact@macromatrix.io">contact@macromatrix.io</a></p>
        <p><strong>Support Email:</strong> <a href="mailto:support@macromatrix.io">support@macromatrix.io</a></p>
        <p><strong>Website:</strong> <a href="https://macromatrix.io/">https://macromatrix.io/</a></p>
      </div>
    </div>
  );
}

export function SecondMarriageCommunityGuidelines() {
  return (
    <div className="sm-legal">
      <SEO title="Community Guidelines – Second Marriage" description="Community guidelines and rules for the Second Marriage matrimonial platform." path="/secondmarriage/communityguidelines" />
      <h1>Community Guidelines for Second Marriage</h1>
      <p className="sm-meta">Last Updated: 3 September 2026</p>

      <p>Welcome to Second Marriage. Second Marriage is a matrimonial and relationship platform designed to help eligible divorced or single adults connect with potential partners for meaningful relationships and marriage.</p>
      <p>We want Second Marriage to be a respectful, safe, and trustworthy community. These Community Guidelines explain what behavior and content are allowed and prohibited when using Second Marriage.</p>
      <p>By using Second Marriage, you agree to follow these Community Guidelines, our Terms of Service, Privacy Policy, and applicable laws. Violations may result in warnings, content removal, feature restrictions, temporary suspension, permanent account removal, or other appropriate action.</p>

      <h2>1. Adults Only</h2>
      <p>Second Marriage is strictly for adults aged 18 years and above. You must not:</p>
      <ul>
        <li>Create an account if you are under 18</li>
        <li>Misrepresent your age</li>
        <li>Upload photographs of minors as your primary dating or matrimonial profile photograph</li>
        <li>Attempt to use the platform for relationships involving minors</li>
        <li>Communicate with minors for romantic or matrimonial purposes</li>
        <li>Share, request, or distribute sexual content involving minors</li>
      </ul>
      <p>Any suspected exploitation or sexual abuse involving a minor may result in immediate account removal and may be reported to relevant authorities where required by law.</p>

      <h2>2. Be Honest About Who You Are</h2>
      <p>Trust is essential in a matrimonial community. You must provide reasonably accurate information about yourself. Do not:</p>
      <ul>
        <li>Create a fake profile</li>
        <li>Pretend to be another person</li>
        <li>Use someone else's photographs without permission</li>
        <li>Misrepresent your identity</li>
        <li>Misrepresent your age</li>
        <li>Misrepresent your gender where the purpose is deception</li>
        <li>Misrepresent your marital status</li>
        <li>Misrepresent information in order to deceive another user</li>
        <li>Create multiple accounts for deceptive purposes</li>
      </ul>
      <p>If we reasonably believe that your account or profile is misleading or fraudulent, we may restrict or remove it.</p>

      <h2>3. Use Your Own Photos</h2>
      <p>Your profile photographs should accurately represent you. Do not upload:</p>
      <ul>
        <li>Someone else's photograph without permission</li>
        <li>Celebrity photographs unless the profile genuinely belongs to you</li>
        <li>Stolen photographs</li>
        <li>AI-generated photographs intended to deceive users into believing they represent a real user</li>
        <li>Images designed to impersonate another person</li>
        <li>Graphic or disturbing images</li>
        <li>Explicit sexual images</li>
        <li>Images involving minors in inappropriate contexts</li>
      </ul>
      <p>Reasonable editing or filters may be permitted, but profile photographs should not intentionally misrepresent your identity.</p>

      <h2>4. Respect Other Users</h2>
      <p>Treat other users with respect. Do not:</p>
      <ul>
        <li>Harass another user</li>
        <li>Bully another user</li>
        <li>Threaten another person</li>
        <li>Stalk another user</li>
        <li>Repeatedly contact someone who has rejected or blocked you</li>
        <li>Use abusive language</li>
        <li>Humiliate another person</li>
        <li>Intentionally intimidate another person</li>
        <li>Encourage self-harm or serious harm</li>
        <li>Target another user because of their personal characteristics</li>
      </ul>
      <p>Disagreement is allowed. Abuse and harassment are not.</p>

      <h2>5. No Hate or Discriminatory Harassment</h2>
      <p>You must not attack, threaten, dehumanize, or harass individuals or groups based on protected characteristics where prohibited by applicable law. This includes discriminatory harassment based on characteristics such as race, ethnicity, nationality, religion, disability, gender, sexual orientation, or other legally protected characteristics.</p>
      <p>Users may have personal compatibility preferences. However, expressing personal preferences does not permit abusive, hateful, humiliating, or discriminatory harassment.</p>

      <h2>6. No Scams or Financial Exploitation</h2>
      <p>Second Marriage must not be used for financial scams or exploitation. Do not:</p>
      <ul>
        <li>Request money from users using deception</li>
        <li>Create false emergencies</li>
        <li>Pretend to have a financial crisis to manipulate another user</li>
        <li>Request banking credentials</li>
        <li>Request passwords</li>
        <li>Request verification codes</li>
        <li>Request payment card information</li>
        <li>Promote fraudulent investment schemes</li>
        <li>Promote cryptocurrency scams</li>
        <li>Attempt identity theft</li>
        <li>Use romance or marriage discussions to financially manipulate another person</li>
      </ul>
      <p>Be cautious if another user requests money, claims to have an emergency, requests cryptocurrency payments, requests gift cards, requests banking information, or pressures you to send money quickly. If you believe someone is attempting to scam you, stop communication and report the account.</p>

      <h2>7. No Sexual Exploitation or Explicit Sexual Content</h2>
      <p>Second Marriage is intended for meaningful relationships and marriage. Do not use the platform to:</p>
      <ul>
        <li>Sexually exploit another user</li>
        <li>Pressure another person into sexual activity</li>
        <li>Share sexually explicit images where prohibited by the Service</li>
        <li>Request sexually explicit images</li>
        <li>Distribute intimate images without consent</li>
        <li>Threaten to distribute intimate images</li>
        <li>Engage in sexual extortion</li>
        <li>Use the Service for prostitution or commercial sexual services where prohibited by law</li>
      </ul>
      <p>Non-consensual intimate content is strictly prohibited.</p>

      <h2>8. Consent Matters</h2>
      <p>Respect personal boundaries. Do not:</p>
      <ul>
        <li>Pressure another user into conversations</li>
        <li>Pressure another person into sharing private information</li>
        <li>Pressure another user into an offline meeting</li>
        <li>Continue contacting someone after they clearly ask you to stop</li>
        <li>Attempt to bypass a block</li>
        <li>Create another account to contact someone who blocked you</li>
      </ul>
      <p>A user may decline communication, reject an interest request, unmatch, block, or stop communicating at any time. Respect their decision.</p>

      <h2>9. Keep Personal Information Safe</h2>
      <p>Protect your own privacy and respect the privacy of others. Do not:</p>
      <ul>
        <li>Share another person's private information without permission</li>
        <li>Publish someone's address</li>
        <li>Publish private phone numbers without authorization</li>
        <li>Share private conversations outside the platform without authorization where prohibited by applicable law</li>
        <li>Share another user's financial information</li>
        <li>Share authentication credentials</li>
        <li>Attempt to obtain private information through deception</li>
      </ul>
      <p>Do not share highly sensitive information with people you do not know and trust.</p>

      <h2>10. No Impersonation</h2>
      <p>You must not impersonate another user, a celebrity, a public figure, a Second Marriage employee, a moderator, a company representative, a law enforcement officer, or any other person or organization.</p>
      <p>You must not falsely claim that your account has been officially endorsed or verified beyond the actual status displayed by Second Marriage.</p>

      <h2>11. Face Verification</h2>
      <p>Second Marriage may provide face or photo verification features. Verification is intended to provide an additional safety signal. A verified status does not guarantee that a person is trustworthy, compatible with you, intends to marry, will behave safely, or that all profile information is accurate.</p>
      <p>Users must not attempt to manipulate or bypass verification systems. Fraudulent attempts to obtain verification may result in account restrictions or removal.</p>

      <h2>12. AI Profiles</h2>
      <p>Some profiles within Second Marriage may be generated or operated using artificial intelligence. These profiles are clearly identified with a visible "AI Profile" label.</p>
      <p>AI Profiles are not real human users, are not genuine matrimonial applicants, are not real marriage proposals, and should not be mistaken for verified human users.</p>
      <p>Users must not remove, hide, alter, or misrepresent AI disclosures where AI-related functionality is made available. Second Marriage does not permit intentionally deceptive AI-operated accounts to be presented as real human users.</p>

      <h2>13. Do Not Misuse AI Features</h2>
      <p>If AI-powered features are available, do not use them to generate scams, impersonate another person, harass another user, create fraudulent profiles, generate illegal content, attempt to manipulate users, circumvent safety systems, or obtain private information.</p>
      <p>AI-generated information may contain errors or inaccuracies. Do not rely solely on AI-generated content when making important personal, financial, legal, relationship, or marriage decisions.</p>

      <h2>14. No Spam</h2>
      <p>Do not:</p>
      <ul>
        <li>Send repeated unwanted messages</li>
        <li>Send the same message to large numbers of users</li>
        <li>Promote unrelated businesses</li>
        <li>Send commercial advertising without authorization</li>
        <li>Send malicious links</li>
        <li>Use automated messaging tools without authorization</li>
        <li>Create multiple accounts for spam purposes</li>
      </ul>
      <p>Repeated spam may result in account restrictions or permanent removal.</p>

      <h2>15. No Illegal Activity</h2>
      <p>Do not use Second Marriage to break applicable laws, plan illegal activity, promote illegal services, commit fraud, commit identity theft, engage in blackmail, threaten violence, facilitate exploitation, or distribute unlawful material. We may cooperate with lawful authorities when required by applicable law.</p>

      <h2>16. No Violence or Threats</h2>
      <p>Do not threaten to harm another person, encourage violence, promote serious physical harm, use the platform to plan violent acts, threaten another user because they rejected you, or use intimidation to force someone into a relationship or marriage.</p>
      <p>Credible threats may result in immediate account suspension or removal. Where required or appropriate, serious threats may be referred to relevant authorities.</p>

      <h2>17. Marriage and Relationship Decisions</h2>
      <p>Second Marriage helps users discover and communicate with potential partners. However, users are responsible for their own relationship decisions. Second Marriage does not approve marriages, guarantee marriages, guarantee compatibility, guarantee the identity of every user, or guarantee the intentions of another user. Users should independently verify important information before making significant personal decisions.</p>

      <h2>18. Offline Meetings</h2>
      <p>If you choose to meet another user offline, you do so at your own discretion. We recommend:</p>
      <ul>
        <li>Meeting initially in a public location</li>
        <li>Informing a trusted person about your plans</li>
        <li>Using independent transportation where possible</li>
        <li>Being cautious about sharing your home address</li>
        <li>Avoiding financial transactions</li>
        <li>Leaving the situation if you feel unsafe</li>
      </ul>
      <p>Second Marriage is not responsible for decisions users make during offline interactions, subject to applicable law.</p>

      <h2>19. Report Harmful Behavior</h2>
      <p>If you encounter behavior that violates these Guidelines, please use the reporting tools available in the Application. You may report fake profiles, impersonation, scams, harassment, threats, inappropriate content, suspected underage users, fraud, and other safety concerns.</p>
      <p>Please provide accurate information when submitting reports. Do not submit intentionally false reports to harass or harm another user.</p>

      <h2>20. Blocking Users</h2>
      <p>You may use blocking tools to prevent unwanted interaction where available. Do not attempt to bypass another user's block by creating another account, using someone else's account, or contacting the user through unauthorized means. Repeated attempts to bypass a block may result in account removal.</p>

      <h2>21. Enforcement</h2>
      <p>We may investigate violations of these Community Guidelines. Depending on the seriousness and circumstances of a violation, we may:</p>
      <ul>
        <li>Provide a warning</li>
        <li>Remove content</li>
        <li>Remove a photograph</li>
        <li>Restrict certain features</li>
        <li>Temporarily suspend an account</li>
        <li>Remove verification status</li>
        <li>Permanently terminate an account</li>
        <li>Take other appropriate safety actions</li>
      </ul>
      <p>We may take immediate action without prior warning where necessary to protect users, investigate serious abuse, prevent harm, or comply with legal obligations.</p>

      <h2>22. Repeat Violations</h2>
      <p>Repeated violations may result in stronger enforcement actions. Users who repeatedly violate these Guidelines may have their accounts permanently removed. Creating a new account to bypass a suspension or permanent ban may also result in removal.</p>

      <h2>23. Appeals</h2>
      <p>Where available, users may request a review of certain moderation or account decisions. Submitting an appeal does not guarantee that an account or content will be restored. We may review available information and make a decision based on our safety policies and applicable laws. Where permitted by law, our decision may be final.</p>

      <h2>24. Changes to These Community Guidelines</h2>
      <p>We may update these Community Guidelines from time to time. If significant changes are made, we may provide notice through the Application, our website, email, or another appropriate method. The Last Updated date indicates when these Guidelines were most recently revised.</p>

      <h2>25. Contact Us</h2>
      <p>If you have questions about these Community Guidelines or need to report a safety concern, please contact:</p>
      <div className="sm-contact">
        <p><strong>App Name:</strong> Second Marriage</p>
        <p><strong>Company:</strong> Macro Matrix PVT Limited</p>
        <p><strong>Support Email:</strong> <a href="mailto:support@macromatrix.io">support@macromatrix.io</a></p>
        <p><strong>Website:</strong> <a href="https://macromatrix.io/">https://macromatrix.io/</a></p>
      </div>
    </div>
  );
}

const FAQ_ITEMS = [
  {
    q: "What is Second Marriage?",
    a: "Second Marriage is a matrimonial app designed for adults who are serious about finding a life partner for marriage — whether divorced, widowed, or never married.",
  },
  {
    q: "Is Second Marriage free to use?",
    a: "The core features — creating a profile, browsing matches, sending interests, and chatting — are free. An optional premium subscription removes ads and unlocks expanded discovery features.",
  },
  {
    q: "How does matching work?",
    a: "You set your partner preferences (age, city, marital status, education, etc.) and browse profiles that match your criteria. When you express interest and the other person accepts, a private chat opens.",
  },
  {
    q: "What is the Guardian (Wali) feature?",
    a: "You can invite a trusted family member or guardian to oversee your search. They can view your profile, see your matches, and help guide the process — in line with family and cultural expectations.",
  },
  {
    q: "How is my privacy protected?",
    a: "Photos, phone numbers, and activity status are private by default. You control what is visible. We never share your data with other users without your consent. Read our full Privacy Policy for details.",
  },
  {
    q: "How do you verify profiles?",
    a: "We offer optional selfie verification — you take a quick selfie and our system confirms it matches your profile photos. Verified users get a badge on their profile. No government ID is required.",
  },
  {
    q: "How do I report or block someone?",
    a: "You can block or report any user directly from their profile or from within a conversation. Blocked users cannot see your profile or contact you. Our moderation team reviews every report within 24 hours.",
  },
  {
    q: "Is my religious or cultural information required?",
    a: "No. Religious and cultural details are entirely optional. You share only what you're comfortable with.",
  },
  {
    q: "Can I delete my account?",
    a: "Yes. You can delete your account at any time from inside the app. All your data will be removed according to our Privacy Policy.",
  },
  {
    q: "When will the app be available?",
    a: "Second Marriage is coming soon to both the App Store and Google Play. Sign up for our waitlist on the home page to be notified when we launch.",
  },
  {
    q: "How do I contact support?",
    a: "Visit our Support page or email us at support@macromatrix.io. We respond to all inquiries within 24 hours.",
  },
];

export function SecondMarriageFaq() {
  return (
    <div className="sm-legal sm-faq-page">
      <SEO title="FAQ – Second Marriage" description="Frequently asked questions about Second Marriage — matchmaking, safety, privacy, and more." path="/secondmarriage/faq" />
      <h1>Frequently Asked Questions</h1>
      <p className="sm-sub" style={{ marginBottom: 32 }}>
        Everything you need to know about Second Marriage.
      </p>
      <div className="sm-faq-list">
        {FAQ_ITEMS.map((item) => (
          <details key={item.q} className="sm-faq-item">
            <summary>{item.q}</summary>
            <p>{item.a}</p>
          </details>
        ))}
      </div>
      <div className="sm-faq-contact">
        <p>Still have questions?</p>
        <NavLink to="/secondmarriage/contact" className="sm-btn sm-btn-primary">
          Contact Support
        </NavLink>
      </div>
    </div>
  );
}

export function SecondMarriageChildSafety() {
  return (
    <div className="sm-legal">
      <SEO title="Child Safety Standards – Second Marriage" description="Child safety standards and zero-tolerance policy for CSAE/CSAM on the Second Marriage platform." path="/secondmarriage/child-safety" />
      <h1>Child Safety Standards</h1>
      <p className="sm-meta">Second Marriage · MacroMatrix (Pvt) Limited · Last updated: 14 October 2026</p>

      <h2>Our commitment</h2>
      <p>Second Marriage has zero tolerance for child sexual abuse and exploitation (CSAE) and child sexual abuse material (CSAM). These standards apply to every user, profile, photo, and message on the Second Marriage app.</p>

      <h2>Adults only</h2>
      <p>Second Marriage is only for adults aged 18 and over. Accounts found to belong to anyone under 18 are removed immediately.</p>

      <h2>What is prohibited</h2>
      <Ul items={[
        "Any content that sexualises, exploits, or endangers minors",
        "Sharing, requesting, or linking to child sexual abuse material (CSAM)",
        "Grooming, or contacting minors for romantic or sexual purposes",
        "Using photos of minors in a sexual or inappropriate context",
        "Any attempt to use Second Marriage to harm a child",
      ]} />

      <h2>How to report</h2>
      <p>In the app: open the profile or message, tap "Report", and choose "Underage user" or "Sexual content".</p>
      <p>By email: <a href="mailto:contact@macromatrix.io">contact@macromatrix.io</a> (subject: "Child Safety").</p>
      <p>Reports can be made without the reported person knowing who made them.</p>

      <h2>What we do when we receive a report</h2>
      <Ul items={[
        "We review child-safety reports as our highest priority.",
        "We remove the content and permanently ban the accounts involved.",
        "We preserve evidence where required by law.",
        "We report confirmed CSAM to the relevant authorities, including the National Center for Missing & Exploited Children (NCMEC) and national law enforcement in Pakistan, the UAE, and other countries where required.",
      ]} />

      <h2>Child safety contact</h2>
      <p>Our designated child-safety contact is <a href="mailto:contact@macromatrix.io">contact@macromatrix.io</a>. This contact can speak about our CSAM prevention practices and compliance.</p>

      <h2>Related policies</h2>
      <p>
        <NavLink to="/secondmarriage/communityguidelines">Community Guidelines</NavLink>
        {" · "}
        <NavLink to="/secondmarriage/privacypolicy">Privacy Policy</NavLink>
        {" · "}
        <NavLink to="/secondmarriage/termsofservices">Terms of Service</NavLink>
      </p>
      <p style={{ marginTop: 16, fontSize: "0.9rem", opacity: 0.7 }}>These standards follow Google Play's Child Safety Standards policy.</p>
    </div>
  );
}
